import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { JsonLd } from 'react-schemaorg';

import { useQuery } from '@apollo/client';
import { FAQPage } from 'schema-dts';

import { breakpoints, FAQ, Text } from '@notino/react-styleguide';
import {
  GetPdFaqQuery,
  GetPdFaqQueryVariables,
} from '@notino/shared/definitions/types';

import messages from '@containers/ProductDetailContainer/messages';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { css } from '@linaria/core';

import getPdFaqQuery from './pdFaq.graphql';

// Alias to satisfy PascalCase JSX lint rule
const Faq = FAQ;

export const PdFaq = () => {
  const { product } = useProductDetailContext();

  const { data } = useQuery<GetPdFaqQuery, GetPdFaqQueryVariables>(
    getPdFaqQuery,
    {
      variables: { masterProductCode: product.masterProductCode },
      ssr: true,
    }
  );

  const productFaqs = data?.pdFaq;

  if (!productFaqs?.length) {
    return null;
  }

  const questions: React.ComponentProps<typeof FAQ>['questions'] =
    productFaqs.map((faq) => ({
      question: faq.q,
      textAnswer: faq.a,
      isQuestionOpenByDefault: true,
    }));

  return (
    <>
      <JsonLd<FAQPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: productFaqs.map((faq) => ({
            '@type': 'Question' as const,
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer' as const,
              text: faq.a,
            },
          })),
        }}
      />
      <div
        className={css`
          margin: 3rem 0;
          padding-inline: 0.9375rem;
          svg {
            min-width: 20px;
          }
          @media (min-width: ${breakpoints.md}) {
            margin: 4rem 0;
          }
        `}
      >
        <Text typography="titleLarge" marginBottom="1rem">
          <FormattedMessage {...messages.faq} />
        </Text>
        <Faq questions={questions} />
      </div>
    </>
  );
};
